import React, { useState, useEffect, useCallback } from 'react';
import styled from 'astroturf/react';
import { Icon } from 'js/design_system/Icon';

interface FilterOption {
  value: string;
  label: string;
  count: number;
}

interface FilterOptions {
  volume_issues: FilterOption[];
  sections: FilterOption[];
  article_types: FilterOption[];
}

interface SelectedFilters {
  volume_issue?: string;
  sections: string[];
  article_types: string[];
}

interface ArticlesFiltersProps {
  filterOptions: FilterOptions;
  selectedFilters: SelectedFilters;
}

const MobileFiltersButton = styled.button`
  display: none;
  background: var(--background-white, white);
  color: var(--text-primary, black);
  border: 1px solid var(--stroke-dark, black);
  padding: var(--spacing-xs-12, 12px) var(--spacing-xs-16, 16px);
  border-radius: var(--corner-radius-radius-lg, 20px);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  width: fit-content;
  align-items: center;
  gap: var(--spacing-xs-8, 8px);
  position: relative;

  @media (max-width: 1279px) {
    display: flex;
  }

  &:hover {
    background: var(--surface-secondary, #f8f9fa);
  }
`;

const DesktopFiltersContainer = styled.div`
  background: transparent;
  padding: var(--spacing-sm-24, 24px);
  margin-bottom: var(--spacing-sm-32, 32px);
  padding-top: var(--spacing-sm-32, 32px);

  @media (max-width: 1279px) {
    display: none;
  }
`;

const MobileFiltersContainer = styled.div`
  display: none;

  @media (max-width: 1279px) {
    display: block;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-white, white);
  z-index: 1000;
  display: flex;
  flex-direction: column;

  @media (min-width: 1280px) {
    display: none;
  }
`;

const ModalContent = styled.div`
  background: var(--background-white, white);
  flex: 1;
  overflow-y: auto;
  position: relative;
  padding: var(--spacing-sm-24, 20px);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm-24, 24px);
  padding-bottom: var(--spacing-xs-16, 16px);
  border-bottom: 1px solid var(--stroke-light, #e5e5e5);
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: var(--text-primary, #070a15);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--inactive-content, #6b7280);
  padding: 0;
  width: var(--spacing-sm-32, 32px);
  height: var(--spacing-sm-32, 32px);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--text-primary, #070a15);
  }
`;

const FilterSection = styled.div`
  margin-bottom: var(--spacing-sm-24, 24px);

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 var(--spacing-xs-12, 12px) 0;
  color: var(--text-primary, #070a15);
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: flex-start;
  padding: var(--spacing-xs-8, 8px) 0;
  border-radius: var(--corner-radius-radius-md, 6px);
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;
  color: var(--text-primary, #070a15);

  &:hover {
    background: var(--surface-secondary, #f8f9fa);
  }

  &.selected {
    background: var(--background-light-blue, #e6f3f6);
    color: var(--primary-brand, #1f7a8c);
  }
`;

const CheckboxInput = styled.input`
  margin-right: var(--spacing-xs-12, 12px);
  width: var(--spacing-xs-16, 16px);
  height: var(--spacing-xs-16, 16px);
  accent-color: var(--primary-brand, #1f7a8c);
  margin-top: var(--spacing-xs-4, 2px);
`;

const CheckboxLabel = styled.span`
  font-size: 14px;
  color: inherit;
`;

const ShowAllLink = styled.a`
  color: var(--primary-brand, #1f7a8c);
  text-decoration: none;
  font-size: 14px;
  cursor: pointer;
  display: block;
  margin-top: var(--spacing-xs-8, 8px);

  &:hover {
    text-decoration: underline;
  }
`;

const ClearAllButton = styled.button`
  color: var(--primary-brand, #1f7a8c);
  background: none;
  border: none;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  margin-bottom: var(--spacing-xs-16, 16px);
  text-align: left;
  width: 100%;

  &:hover {
    text-decoration: underline;
  }
`;

const ArticlesFilters: React.FC<ArticlesFiltersProps> = ({
  filterOptions,
  selectedFilters,
}) => {
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    volume_issues: false,
    sections: false,
    article_types: false,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [volumeIssues, setVolumeIssues] = useState<Set<string>>(
    new Set(
      selectedFilters.volume_issue
        ? selectedFilters.volume_issue.split(';').filter(Boolean)
        : [],
    ),
  );
  const [sections, setSections] = useState<Set<string>>(
    new Set(selectedFilters.sections || []),
  );
  const [articleTypes, setArticleTypes] = useState<Set<string>>(
    new Set(selectedFilters.article_types || []),
  );

  const getVisibleOptions = (
    options: FilterOption[],
    selectedSet: Set<string>,
    sectionKey: string,
  ) => {
    const selectedOptions = options.filter((option) =>
      selectedSet.has(option.value),
    );
    const unselectedOptions = options.filter(
      (option) => !selectedSet.has(option.value),
    );

    if (expandedSections[sectionKey]) {
      return options;
    }

    const visibleUnselected = unselectedOptions.slice(0, 4);
    return [...selectedOptions, ...visibleUnselected];
  };

  const shouldShowMore = (
    options: FilterOption[],
    selectedSet: Set<string>,
    sectionKey: string,
  ) => {
    if (expandedSections[sectionKey]) {
      return false;
    }

    const unselectedOptions = options.filter(
      (option) => !selectedSet.has(option.value),
    );

    return unselectedOptions.length > 4;
  };

  const toggleExpanded = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    // Initialize state from URL parameters only, not from props
    const urlParams = new URLSearchParams(window.location.search);

    const urlVolumeIssue = urlParams.get('volume_issue');
    if (urlVolumeIssue) {
      const urlVolumeIssuesSet = new Set(
        urlVolumeIssue.split(';').filter(Boolean),
      );
      setVolumeIssues(urlVolumeIssuesSet);
    }

    const urlSections = urlParams.get('sections');
    if (urlSections) {
      const urlSectionsSet = new Set(urlSections.split(';').filter(Boolean));
      setSections(urlSectionsSet);
    }

    const urlArticleTypes = urlParams.get('article_types');
    if (urlArticleTypes) {
      const urlArticleTypesSet = new Set(
        urlArticleTypes.split(';').filter(Boolean),
      );
      setArticleTypes(urlArticleTypesSet);
    }
  }, []); // Only run on mount, not when props change

  const updateUrl = useCallback(
    (
      newVolumeIssues: Set<string>,
      newSections: Set<string>,
      newArticleTypes: Set<string>,
    ) => {
      // Start from existing params to preserve sort and other parameters
      const params = new URLSearchParams(window.location.search);

      // Clear filter params before re-adding
      params.delete('volume_issue');
      params.delete('sections');
      params.delete('article_types');

      if (newVolumeIssues.size > 0) {
        const volumeIssueString = Array.from(newVolumeIssues).join(';');
        params.set('volume_issue', volumeIssueString);
      }

      if (newSections.size > 0) {
        const sectionsString = Array.from(newSections).join(';');
        params.set('sections', sectionsString);
      }

      if (newArticleTypes.size > 0) {
        const articleTypesString = Array.from(newArticleTypes).join(';');
        params.set('article_types', articleTypesString);
      }

      const currentUrl = window.location.pathname;
      const newUrl = params.toString()
        ? `${currentUrl}?${params.toString()}`
        : currentUrl;
      window.location.href = newUrl;
    },
    [],
  );

  const handleVolumeIssueChange = (value: string, checked: boolean) => {
    const newVolumeIssues = new Set(volumeIssues);
    if (checked) {
      newVolumeIssues.add(value);
    } else {
      newVolumeIssues.delete(value);
    }
    setVolumeIssues(newVolumeIssues);

    // Update URL and refresh page
    updateUrl(newVolumeIssues, sections, articleTypes);
  };

  const handleSectionsChange = (value: string, checked: boolean) => {
    const newSections = new Set(sections);
    if (checked) {
      newSections.add(value);
    } else {
      newSections.delete(value);
    }
    setSections(newSections);

    // Update URL and refresh page
    updateUrl(volumeIssues, newSections, articleTypes);
  };

  const handleArticleTypesChange = (value: string, checked: boolean) => {
    const newArticleTypes = new Set(articleTypes);
    if (checked) {
      newArticleTypes.add(value);
    } else {
      newArticleTypes.delete(value);
    }
    setArticleTypes(newArticleTypes);

    // Update URL and refresh page
    updateUrl(volumeIssues, sections, newArticleTypes);
  };

  const clearAllFilters = () => {
    setVolumeIssues(new Set());
    setSections(new Set());
    setArticleTypes(new Set());

    // Update URL and refresh page
    updateUrl(new Set(), new Set(), new Set());
  };

  const hasActiveFilters =
    volumeIssues.size > 0 || sections.size > 0 || articleTypes.size > 0;

  const renderFilters = () => (
    <>
      {hasActiveFilters && (
        <ClearAllButton onClick={clearAllFilters}>
          Clear all filters
        </ClearAllButton>
      )}

      <FilterSection>
        <FilterTitle>Volume and Issue</FilterTitle>
        <CheckboxList>
          {getVisibleOptions(
            filterOptions.volume_issues,
            volumeIssues,
            'volume_issues',
          ).map((option) => {
            const isSelected = volumeIssues.has(option.value);
            return (
              <CheckboxItem
                key={option.value}
                className={isSelected ? 'selected' : ''}
              >
                <CheckboxInput
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) =>
                    handleVolumeIssueChange(option.value, e.target.checked)
                  }
                />
                <CheckboxLabel>
                  {option.label} ({option.count})
                </CheckboxLabel>
              </CheckboxItem>
            );
          })}
        </CheckboxList>
        {shouldShowMore(
          filterOptions.volume_issues,
          volumeIssues,
          'volume_issues',
        ) && (
          <ShowAllLink onClick={() => toggleExpanded('volume_issues')}>
            Show all volumes and issues
          </ShowAllLink>
        )}
      </FilterSection>

      <FilterSection>
        <FilterTitle>Section</FilterTitle>
        <CheckboxList>
          {getVisibleOptions(filterOptions.sections, sections, 'sections').map(
            (option) => {
              const isSelected = sections.has(option.value);
              return (
                <CheckboxItem
                  key={option.value}
                  className={isSelected ? 'selected' : ''}
                >
                  <CheckboxInput
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) =>
                      handleSectionsChange(option.value, e.target.checked)
                    }
                  />
                  <CheckboxLabel>
                    {option.label} ({option.count})
                  </CheckboxLabel>
                </CheckboxItem>
              );
            },
          )}
        </CheckboxList>
        {shouldShowMore(filterOptions.sections, sections, 'sections') && (
          <ShowAllLink onClick={() => toggleExpanded('sections')}>
            Show all sections
          </ShowAllLink>
        )}
      </FilterSection>

      <FilterSection>
        <FilterTitle>Article type</FilterTitle>
        <CheckboxList>
          {getVisibleOptions(
            filterOptions.article_types,
            articleTypes,
            'article_types',
          ).map((option) => {
            const isSelected = articleTypes.has(option.value);
            return (
              <CheckboxItem
                key={option.value}
                className={isSelected ? 'selected' : ''}
              >
                <CheckboxInput
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) =>
                    handleArticleTypesChange(option.value, e.target.checked)
                  }
                />
                <CheckboxLabel>
                  {option.label} ({option.count})
                </CheckboxLabel>
              </CheckboxItem>
            );
          })}
        </CheckboxList>
        {shouldShowMore(
          filterOptions.article_types,
          articleTypes,
          'article_types',
        ) && (
          <ShowAllLink onClick={() => toggleExpanded('article_types')}>
            Show all article types
          </ShowAllLink>
        )}
      </FilterSection>
    </>
  );

  return (
    <>
      <DesktopFiltersContainer>{renderFilters()}</DesktopFiltersContainer>

      <MobileFiltersContainer>
        <MobileFiltersButton onClick={openModal}>
          <Icon name="instant_mix" />
          Filters{' '}
          {hasActiveFilters &&
            `(${volumeIssues.size + sections.size + articleTypes.size})`}
        </MobileFiltersButton>

        {isModalOpen && (
          <ModalOverlay onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Filters</ModalTitle>
                <CloseButton onClick={closeModal}>&times;</CloseButton>
              </ModalHeader>
              {renderFilters()}
            </ModalContent>
          </ModalOverlay>
        )}
      </MobileFiltersContainer>
    </>
  );
};

export default ArticlesFilters;
